import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService {
  private readonly isLoadedSubject = new BehaviorSubject<boolean>(false);
  public readonly isLoaded$ = this.isLoadedSubject.asObservable();
  
  private loadingPromise: Promise<void> | null = null;

  constructor() {
    // Check if Google Maps is already loaded (in case of page refresh)
    if (typeof google !== 'undefined' && google?.maps) {
      this.isLoadedSubject.next(true);
    }
  }

  /**
   * Load Google Maps API script
   */
  loadScript(apiKey: string): Promise<void> {
    // If already loaded, return immediately
    if (this.isLoadedSubject.value) {
      return Promise.resolve();
    }

    // If currently loading, return the existing promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Start loading
    this.loadingPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?libraries=places,geometry,marker&key=${apiKey}`;
      
      script.onload = () => {
        this.isLoadedSubject.next(true);
        resolve();
      };

      script.onerror = (error) => {
        const errorMessage = error instanceof Error ? error : new Error('Failed to load Google Maps script');
        console.error('Error loading Google Maps script:', errorMessage);
        this.loadingPromise = null; // Allow retry
        reject(errorMessage);
      };

      document.getElementsByTagName('head')[0].appendChild(script);
    });

    return this.loadingPromise;
  }
  isLoaded(): boolean {
    return this.isLoadedSubject.value;
  }
}
